import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Button,
  Stack,
  Collapse,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MicNoneIcon from '@mui/icons-material/MicNone';
import StopIcon from '@mui/icons-material/Stop';
import OutlinedButton from '../../ui-library/OutlinedButton';
import {
  primitiveColors,
  semanticColors,
  semanticGradients,
  BORDER_RADIUS,
  BORDER_RADIUS_LG,
} from '@/theme/tokens';
import { getBrandGradientWithOpacity } from '@/theme/utils';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { VoiceRecordingBar } from './VoiceRecordingBar';
import type { SuggestedPrompt } from './ChatLandingState';
import {
  HandThumbUpIcon as HandThumbUpOutline,
  HandThumbDownIcon as HandThumbDownOutline,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolid,
  HandThumbDownIcon as HandThumbDownSolid,
} from '@heroicons/react/24/solid';

import { SourceItem } from './SourceTypes';
import type { NarrationEvent } from './NarrationTypes';
import type { AnswerDepth, ResearchDepth } from './AnswerDepth';
import type { SendChatOptions } from '@/contexts/ChatContext';
import AnswerDepthSelector from './AnswerDepthSelector';
import useBallotLoading, { isBallotLoading } from '@/hooks/useBallotLoading';

import SourceList from './SourceList';
import { FeedbackModal } from './FeedbackModal';

export interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  isLoading?: boolean;
  loadingMessage?: string;
  sources?: SourceItem[];
  // Legacy: the concatenated narration string. Nothing writes or reads it any
  // more — ``narrationEvents`` replaced it, and ``foldNarration`` handles an
  // unlabelled frame too, so there was no case left where the string was the
  // only representation. Declared so previously-persisted messages still type,
  // and dropped from guest storage. Remove once no stored message carries it.
  narrationText?: string;
  // Narration as a structured list, so tool steps and Claude's reasoning can
  // render differently. Not cleared when the answer lands —
  // ``ReasoningDisclosure`` keeps it behind a collapsed control so the reader
  // can go back to the reasoning.
  narrationEvents?: NarrationEvent[];
  // What the backend reports about depth on an assistant message: the depth it
  // ran under, and which research agent actually answered. Both come from the
  // stream rather than from local state, so the label reports what happened
  // rather than what the client asked for.
  answerDepth?: AnswerDepth;
  researchDepth?: ResearchDepth;
  // The language the research trail renders in when the answer was written in
  // a language other than the UI's (the voter asked in Spanish under an English
  // UI). Unset when the two agree, so the trail follows the UI — see
  // ``trailLanguageFor``.
  trailLanguage?: string;
  // Set to true when the backend emits a ``memory_updated`` SSE event
  // for this turn — i.e. at least one new durable memory was extracted
  // and persisted from the user's message. Drives the "Memory updated"
  // pill below the assistant response.
  memoryUpdated?: boolean;
  // Set when the turn was stopped before any answer text arrived, so the
  // bubble holds the cancellation notice rather than a response. There is
  // nothing to rate or copy in that case, so the feedback and copy controls
  // stay hidden.
  noAnswer?: boolean;
}

interface MessageActionsProps {
  onFeedbackSubmit?: (feedbackType: 'thumbs_up' | 'thumbs_down', feedback: string) => void;
  onCopy?: () => void;
  messageText: string;
  showConfirmation?: 'thumbs_up' | 'thumbs_down';
  sources?: SourceItem[];
  /**
   * Whether the body's chips print the numbers the Sources rows carry. False
   * on a turn answered without citations, where the rows are provenance and a
   * number would point at a chip that was never rendered. Passed down from the
   * renderer that already resolved the markers, so the panel cannot disagree
   * with the message above it.
   */
  sourcesAreNumbered?: boolean;
  /**
   * How many of `sources` a chip in the body points at. Those lead the array,
   * so rows past this index are the turn's remaining provenance and render
   * without a number. Defaults to every source, for a caller whose list is
   * cited-only.
   */
  citedCount?: number;
  conversationId?: string | null;
  messageId?: string;
  userId?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  onFeedbackSubmit,
  onCopy,
  messageText,
  showConfirmation,
  sources = [],
  sourcesAreNumbered = true,
  citedCount,
  conversationId,
  messageId,
  userId,
}) => {
  const { t } = useTranslation('chat');
  // Create a unique key for localStorage based on conversation and message
  const storageKey = `sources-expanded-${conversationId}-${messageId}`;

  // Initialize state from localStorage if available
  const [sourcesExpanded, setSourcesExpanded] = React.useState(() => {
    if (typeof window !== 'undefined' && conversationId && messageId) {
      const saved = localStorage.getItem(storageKey);
      return saved === 'true';
    }
    return false;
  });

  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedFeedbackType, setSelectedFeedbackType] = React.useState<
    'thumbs_up' | 'thumbs_down'
  >('thumbs_up');

  // BotMessage already passes sanitized + cited sources; treat as final.
  const safeSources = sources ?? [];

  const handleCopy = () => {
    const lines = messageText.split('\n');
    const plainLines: string[] = [];
    const htmlParts: string[] = [];
    let inTable = false;

    for (const line of lines) {
      const stripped = line.trim();

      // Skip table separator rows
      if (/^\|[\s-:|]+\|$/.test(stripped)) continue;

      // Table rows
      if (/^\|/.test(stripped) && /\|$/.test(stripped)) {
        const cells = stripped
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((c) =>
            c
              .trim()
              .replace(/\*{1,2}/g, '')
              .replace(/\[(\d+)\]/g, '')
          );

        if (!inTable) {
          inTable = true;
          htmlParts.push(
            '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse">'
          );
          // First row is header
          htmlParts.push('<tr>' + cells.map((c) => `<th>${c}</th>`).join('') + '</tr>');
        } else {
          htmlParts.push('<tr>' + cells.map((c) => `<td>${c}</td>`).join('') + '</tr>');
        }
        plainLines.push(cells.join('\t'));
        continue;
      }

      // Close table if we were in one
      if (inTable) {
        inTable = false;
        htmlParts.push('</table>');
      }

      // Clean line for plain text
      const cleanLine = line
        .replace(/\*{1,2}/g, '')
        .replace(/#{1,6}\s*/g, '')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\[(\d+)\]/g, '');

      plainLines.push(cleanLine);

      // Build HTML for non-table lines
      const htmlLine = line
        .replace(/\[(\d+)\]/g, '')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');

      if (/^#{1,6}\s/.test(stripped)) {
        const level = stripped.match(/^(#{1,6})/)?.[1].length || 2;
        const text = htmlLine.replace(/^#{1,6}\s+/, '');
        htmlParts.push(`<h${level}>${text}</h${level}>`);
      } else if (/^\s*[-*]\s+/.test(line)) {
        htmlParts.push(`<li>${htmlLine.replace(/^\s*[-*]\s+/, '')}</li>`);
      } else if (stripped) {
        htmlParts.push(`<p>${htmlLine}</p>`);
      }
    }

    if (inTable) htmlParts.push('</table>');

    const plain = plainLines
      .join('\n')
      .replace(/^\s*[-]\s+/gm, '- ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const html = htmlParts.join('\n');

    // Write both rich text and plain text to clipboard
    const blob = new Blob([html], { type: 'text/html' });
    const plainBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard
      .write([new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })])
      .then(() => {
        if (onCopy) onCopy();
      })
      .catch(() => {
        // Clipboard write failed (permission denied or page not focused); do not signal success
      });
  };

  const handleToggleSources = () => {
    const newExpanded = !sourcesExpanded;
    setSourcesExpanded(newExpanded);

    // Persist to localStorage if we have the necessary keys
    if (typeof window !== 'undefined' && conversationId && messageId) {
      localStorage.setItem(storageKey, String(newExpanded));
    }
  };

  const handleFeedbackClick = (feedbackType: 'thumbs_up' | 'thumbs_down') => {
    setSelectedFeedbackType(feedbackType);
    setModalOpen(true);
  };

  const handleFeedbackSubmit = (feedback: string) => {
    if (onFeedbackSubmit) {
      onFeedbackSubmit(selectedFeedbackType, feedback);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  if (!conversationId) return null;

  return (
    <Box>
      <Box display="flex" flexWrap="nowrap" gap={0} mt={2} alignItems="center">
        {/* Thumbs up */}
        {(!showConfirmation || showConfirmation === 'thumbs_up') && (
          <IconButton
            size="small"
            title={t('messageActions.positiveFeedback')}
            aria-label={t('messageActions.positiveFeedback')}
            aria-disabled={showConfirmation ? 'true' : undefined}
            tabIndex={showConfirmation ? -1 : 0}
            onClick={showConfirmation ? undefined : () => handleFeedbackClick('thumbs_up')}
            sx={{
              color: semanticColors.textCaption,
              cursor: showConfirmation ? 'default' : 'pointer',
              '&:hover': { color: semanticColors.textPrimary },
            }}
          >
            {showConfirmation === 'thumbs_up' ? (
              <HandThumbUpSolid width={18} height={18} />
            ) : (
              <HandThumbUpOutline width={18} height={18} />
            )}
          </IconButton>
        )}
        {/* Thumbs down */}
        {(!showConfirmation || showConfirmation === 'thumbs_down') && (
          <IconButton
            size="small"
            title={t('messageActions.negativeFeedback')}
            aria-label={t('messageActions.negativeFeedback')}
            aria-disabled={showConfirmation ? 'true' : undefined}
            tabIndex={showConfirmation ? -1 : 0}
            onClick={showConfirmation ? undefined : () => handleFeedbackClick('thumbs_down')}
            sx={{
              color: semanticColors.textCaption,
              cursor: showConfirmation ? 'default' : 'pointer',
              '&:hover': { color: semanticColors.textPrimary },
            }}
          >
            {showConfirmation === 'thumbs_down' ? (
              <HandThumbDownSolid width={18} height={18} />
            ) : (
              <HandThumbDownOutline width={18} height={18} />
            )}
          </IconButton>
        )}
        {/* Copy */}
        <IconButton
          size="small"
          title={t('messageActions.copyMessage')}
          aria-label={t('messageActions.copyMessage')}
          onClick={handleCopy}
          sx={{
            color: semanticColors.textCaption,
            '&:hover': { color: semanticColors.textPrimary },
          }}
        >
          <ClipboardDocumentIcon width={18} height={18} />
        </IconButton>

        {/* Sources toggle */}
        {safeSources.length > 0 && (
          <Button
            size="small"
            title={t('messageActions.viewSources')}
            aria-expanded={sourcesExpanded}
            aria-controls={`sources-panel-${messageId}`}
            onClick={handleToggleSources}
            sx={{
              color: semanticColors.textCaption,
              textTransform: 'none',
              fontSize: 12,
              minWidth: 'auto',
              ml: 1,
              px: 1.25,
              py: 0.25,
              // Outlined chip: standard border + radius token, no fill.
              border: `1px solid ${primitiveColors.rockSlate}33`,
              borderRadius: BORDER_RADIUS,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
                borderColor: `${primitiveColors.rockSlate}66`,
                color: semanticColors.textPrimary,
              },
            }}
          >
            {t('messageActions.sources', { count: safeSources.length })}
          </Button>
        )}
      </Box>

      {/* Sources panel */}
      {safeSources.length > 0 && (
        <Collapse
          id={`sources-panel-${messageId}`}
          in={sourcesExpanded}
          timeout="auto"
          unmountOnExit
        >
          <Box mt={2}>
            <SourceList
              sources={safeSources}
              numbered={sourcesAreNumbered}
              citedCount={citedCount}
            />
          </Box>
        </Collapse>
      )}

      <Box role="status" aria-live="polite" aria-atomic="true">
        {showConfirmation && (
          <Typography
            variant="caption"
            sx={{
              color: primitiveColors.rockSlate,
              fontSize: 12,
              mt: 0.5,
              display: 'block',
              opacity: 0.8,
            }}
          >
            {t('messageActions.feedbackConfirmed')}
          </Typography>
        )}
      </Box>

      <FeedbackModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleFeedbackSubmit}
        feedbackType={selectedFeedbackType}
        conversationId={conversationId}
        messageId={messageId}
        userId={userId}
      />
    </Box>
  );
};

interface ChatSuggestionProps {
  /**
   * The follow-up chips shown above the composer. Carries the whole prompt
   * rather than its text so a tap can report which template it came from.
   */
  suggestions: SuggestedPrompt[];
  onSuggestionClick: (suggestion: SuggestedPrompt, rank: number) => void;
  loading?: boolean;
}

const LOADING_OPACITY_HEX = '80'; // 50% opacity (0x80 / 0xFF)

export const ChatSuggestions: React.FC<ChatSuggestionProps> = ({
  suggestions,
  onSuggestionClick,
  loading,
}) => {
  const { t } = useTranslation('chat');
  const ballotLoading = useBallotLoading();
  const chipsDisabled = Boolean(loading) || ballotLoading;

  // An empty row would still paint its scrim as a bare band above the
  // composer, so render nothing when there is nothing to suggest.
  if (suggestions.length === 0) return null;

  return (
    <Stack
      direction="row"
      flexWrap="nowrap"
      useFlexGap
      role="group"
      aria-label={t('landing.suggestedActionsLabel')}
      // Safari has a history of not auto-scrolling a horizontal overflow
      // container when focus lands on a child, which would leave a keyboard
      // user's focus ring rendered off-screen (SC 2.4.11). React's onFocus
      // bubbles, so one handler here covers every chip; `nearest` keeps it
      // from scrolling the page as a side effect.
      onFocus={(event) => {
        (event.target as HTMLElement).scrollIntoView({
          inline: 'nearest',
          block: 'nearest',
        });
      }}
      sx={{
        // The surface and the fade into the transcript are carried by the
        // sticky block in ChatWindow, which spans the chips AND the composer —
        // covering only this row left the answer showing through the space
        // between them.
        position: 'relative',
        // ONE row, scrolled horizontally. A wrapping row grew to three stacked
        // lines whenever the labels were long — and the labels carry election
        // and candidate names, so their length is not something this layout
        // controls. A strip bounds the height at one chip whatever the copy
        // does. Scrolling a contained widget is fine for WCAG 1.4.10, which is
        // about the page needing two-dimensional scrolling.
        overflowX: 'auto',
        overflowY: 'hidden',
        // A hidden scrollbar leaves a mouse-only user who cannot perform a
        // horizontal drag or wheel gesture with no way to reach later chips.
        // Touch pointers swipe, so the bar is hidden there and kept as a thin
        // drag target wherever the pointer is fine.
        scrollbarWidth: 'thin',
        '@media (pointer: coarse)': {
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': {
          background: semanticColors.chatBotMessageHover,
          borderRadius: 2,
        },
        // Left-aligned so the highest-ranked follow-up is the one already on
        // screen; the rest are a swipe away.
        justifyContent: 'flex-start',
        // Fixed px, not the spacing scale: this theme maps spacing(1) to 16px,
        // so the previous `py: 0.5` gave only 8px — less than the ~8.5px the
        // 44px ::after below needs to bleed past a ~27px pill, and
        // `overflowY: hidden` clips whatever does not fit. 12px leaves the
        // touch target room to actually be 44px rather than nominally so.
        py: '12px',
        // The chips read as part of the composer, so they sit just above it.
        mb: '8px',
        '&.MuiStack-root': {
          gap: 1,
        },
      }}
    >
      {suggestions.map((s, i) => (
        <OutlinedButton
          key={`${s.id}:${i}`}
          onClick={() => onSuggestionClick(s, i)}
          gradientOutline={true}
          disabled={chipsDisabled}
          styleOverrides={{
            root: {
              borderRadius: BORDER_RADIUS,
              background: chipsDisabled
                ? getBrandGradientWithOpacity(LOADING_OPACITY_HEX)
                : semanticGradients.primary,
              padding: '1px',
              flexShrink: 0,
            },
            outlinedButton: {
              background: semanticColors.chatBotMessage,
              color: `${primitiveColors.rockSlate}${chipsDisabled ? LOADING_OPACITY_HEX : ''}`,
              border: '0.5px solid transparent',
              borderRadius: BORDER_RADIUS,
              // ~30px tall: well above the 24px WCAG 2.2 AA target minimum,
              // with the touch target held at 44px by the ::after below so it
              // does not shrink along with the visuals.
              fontSize: 13,
              padding: '5px 12px',
              // MUI's Button line-height is 1.75, which dominated the pill's
              // height and left the font/padding reduction worth only ~3px.
              // 1.25 is what actually shrinks it, and keeps 13px text legible.
              lineHeight: 1.25,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 44,
              },
              '&:hover': {
                background: semanticColors.chatBotMessageHover,
                color: `${primitiveColors.rockSlate}${chipsDisabled ? LOADING_OPACITY_HEX : ''}`,
              },
              // The chip already carries a gradient border, so the default UA
              // ring reads poorly against it. Matches the composer's send
              // button rather than inventing a second focus treatment.
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: 2,
              },
            },
          }}
        >
          {s.label}
        </OutlinedButton>
      ))}
    </Stack>
  );
};

interface ChatInputProps {
  onSend: (value: string, options?: SendChatOptions) => void | Promise<void>;
  onStop?: () => void;
  isResponding?: boolean;
  disabled?: boolean;
  placeholder?: string;
  showHelper?: boolean;
  /**
   * Answer depth for the next message, and its setter. Owned by
   * ``ChatContainer`` so the choice survives re-renders of the input and
   * applies to every message in the session, not just the next one.
   */
  depth?: AnswerDepth;
  onDepthChange?: (depth: AnswerDepth) => void;
  /**
   * When true, the composer's top corners render flush (no border-radius)
   * so it docks cleanly under whatever sits directly on top — specifically
   * the ``BallotGrabber`` / ``BallotSheet``'s teal header in the active-chat
   * layout. Default ``false``: the composer keeps all four rounded corners
   * (landing state, no sheet above).
   */
  flushTop?: boolean;
  /**
   * Where this composer is mounted, for voice-input analytics segmentation.
   * Defaults to the active-chat case.
   */
  voiceEntryPoint?: 'landing' | 'conversation';
}

// 1000 rather than 500 because voice input lands whole utterances in the
// composer: ~60 seconds of speech is roughly 800 characters, so the old
// ceiling truncated ordinary spoken questions mid-sentence. Mirrors the
// backend's ChatRequest validator and the mobile app's MAX_CHAT_INPUT_LENGTH.
const MAX_MESSAGE_LENGTH = 1000;

// Soft, downward-biased shadow so the composer reads as a card floating above
// the page. The negative spread keeps the blur from bleeding upward onto the
// teal grabber that docks on top of the composer in the active-chat layout.
const COMPOSER_SHADOW = `0 1px 2px ${semanticColors.shadowLight}, 0 10px 28px -8px rgba(12, 47, 55, 0.22)`;

export const ChatInput = React.memo(function ChatInput({
  onSend,
  onStop,
  isResponding = false,
  disabled,
  placeholder,
  showHelper = true,
  flushTop = false,
  depth,
  onDepthChange,
  voiceEntryPoint = 'conversation',
}: ChatInputProps) {
  const { t } = useTranslation('chat');
  const effectivePlaceholder = placeholder ?? t('input.placeholder');
  const [value, setValue] = useState('');
  // Typing stays open while the ballot loads; only sending waits for it.
  const ballotLoading = useBallotLoading();
  const characterCount = value.length;
  // ``maxLength`` already stops typing at the limit; the flag only colors the
  // counter so a voter sees why their text stopped growing.
  const atLimit = characterCount >= MAX_MESSAGE_LENGTH;
  const showCount = characterCount >= MAX_MESSAGE_LENGTH * 0.8;
  const canSend = value.trim().length > 0 && !disabled && !isResponding && !ballotLoading;
  const canStop = isResponding && !!onStop;
  const actionLabel = t(canStop ? 'input.stopResponse' : 'input.sendMessage');

  // How the current draft was composed. A transcript left in the composer for
  // review is still a spoken question when it is finally sent, so the draft
  // carries its method to the send; clearing the draft starts over as typed.
  const draftInputMethodRef = React.useRef<'text' | 'voice'>('text');
  React.useEffect(() => {
    if (!value) draftInputMethodRef.current = 'text';
  }, [value]);

  // A transcript either lands in the composer for review (the stop button) or
  // sends straight away (the send button). Review is the default because a
  // mangled candidate name sends the agent to research the wrong person, and
  // reading the line first is a cheaper fix than a wrong answer.
  const handleTranscript = useCallback(
    async (text: string, { autoSend }: { autoSend: boolean }) => {
      // A spoken send goes out only when a typed one could: ChatContainer drops
      // sends while the ballot loads or an answer streams, the server refuses
      // a message past the composer's limit, and clearing the field first
      // would lose the transcript. Otherwise it waits in the composer, and it
      // comes back there if the send fails.
      if (
        autoSend &&
        !disabled &&
        !isResponding &&
        !isBallotLoading() &&
        text.length <= MAX_MESSAGE_LENGTH
      ) {
        setValue('');
        try {
          await onSend(text, { inputMethod: 'voice' });
        } catch (error) {
          console.error('Failed to send chat message:', error);
          draftInputMethodRef.current = 'voice';
          setValue(text);
        }
        return;
      }
      // Appended, not replaced: a voter may have typed part of the question
      // before deciding to speak the rest. Trimmed to the composer's limit so
      // the field never holds more than it will send.
      draftInputMethodRef.current = 'voice';
      setValue((prev) => (prev ? `${prev.trim()} ${text}` : text).slice(0, MAX_MESSAGE_LENGTH));
    },
    [onSend, disabled, isResponding]
  );

  const voice = useVoiceInput({
    onTranscript: handleTranscript,
    entryPoint: voiceEntryPoint,
  });
  const isRecordingUi = voice.isRecording || voice.isTranscribing;

  // Typing or sending is the "type your question" the voice error suggests, so
  // either one retires it rather than leaving a stale failure under the answer.
  const { clearError: clearVoiceError } = voice;
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      clearVoiceError();
      setValue(e.target.value);
    },
    [clearVoiceError]
  );

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    clearVoiceError();
    const message = value;
    setValue('');
    try {
      await onSend(message, { inputMethod: draftInputMethodRef.current });
    } catch (error) {
      console.error('Failed to send chat message:', error);
    }
  }, [value, canSend, onSend, clearVoiceError]);

  return (
    <Box
      sx={{
        background: primitiveColors.blue1,
        borderTopLeftRadius: flushTop ? 0 : BORDER_RADIUS_LG,
        borderTopRightRadius: flushTop ? 0 : BORDER_RADIUS_LG,
      }}
    >
      {/* Composer card, two rows: the text on top with the full width to
          itself, the controls on their own row beneath it. A one-line message
          gives a ~94px card, so the 16px text reads at ease instead of being
          squeezed between the buttons. The brand ring appears only while
          something inside has focus. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pt: '6px',
          pb: '8px',
          pl: '16px',
          pr: '8px',
          boxSizing: 'border-box',
          backgroundColor: semanticColors.surface,
          borderRadius: flushTop ? `0 0 ${BORDER_RADIUS_LG} ${BORDER_RADIUS_LG}` : BORDER_RADIUS_LG,
          border: `1px solid ${semanticColors.composerBorder}`,
          boxShadow: COMPOSER_SHADOW,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:focus-within': {
            borderColor: semanticColors.brandPrimary,
            boxShadow: `${COMPOSER_SHADOW}, 0 0 0 3px ${semanticColors.brandPrimary}26`,
          },
        }}
      >
        <InputBase
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          placeholder={effectivePlaceholder}
          value={value}
          onChange={handleChange}
          inputProps={{
            maxLength: MAX_MESSAGE_LENGTH,
            'data-testid': 'chat-input',
            'aria-label': t('input.ariaLabel', { maxLength: MAX_MESSAGE_LENGTH }),
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{
            width: '100%',
            py: '10px',
            pr: '8px',
            // 16px is iOS Safari's "no-auto-zoom" threshold — any input
            // smaller than that triggers a viewport zoom when it receives
            // focus.
            fontSize: 16,
            lineHeight: 1.5,
            color: semanticColors.textPrimary,
            '& textarea': { py: 0 },
            '& textarea::placeholder': {
              color: semanticColors.textPlaceholder,
              opacity: 1,
            },
          }}
        />
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '6px',
          }}
        >
          {voice.isAvailable && isRecordingUi ? (
            <VoiceRecordingBar
              isTranscribing={voice.isTranscribing}
              level={voice.level}
              remainingSeconds={voice.remainingSeconds}
              onCancel={voice.cancel}
              onStop={voice.stopAndInsert}
              onStopAndSend={voice.stopAndSend}
            />
          ) : (
            <>
              {/* Count centered across the controls row as a non-interactive overlay,
              so the depth selector and send button stay grouped on the right.
              Live from the start and never removed from the tree so its first
              appearance is announced. */}
              <Typography
                variant="caption"
                aria-live="polite"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  fontSize: 11,
                  lineHeight: 1.4,
                  color: atLimit ? 'error.main' : semanticColors.textTertiary,
                  opacity: showCount ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none',
                }}
              >
                {showCount ? `${characterCount}/${MAX_MESSAGE_LENGTH}` : ''}
              </Typography>
              {depth && onDepthChange && (
                /* Depth applies to the next turn, so keep it selectable while a
               response is streaming — only the input/send button follow `disabled`. */
                <AnswerDepthSelector depth={depth} onChange={onDepthChange} />
              )}
              {voice.isAvailable && (
                /* Speaking is the shortcut past the composer's narrowest point: a
               text box on a phone, often in a second language. The mic keeps the
               depth selector's quiet caption-grey treatment — it is another way
               in, not a competitor to send. Hidden entirely where the browser
               cannot record, which is also what keeps it out of partner embeds
               whose iframe was never granted microphone access. */
                <IconButton
                  onClick={voice.start}
                  disabled={disabled}
                  aria-label={t('input.mic.start')}
                  data-testid="voice-start-button"
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    color: semanticColors.textCaption,
                    backgroundColor: semanticColors.surfaceWarm,
                    transition: 'background-color 0.2s ease, transform 0.1s ease',
                    '&:hover': { backgroundColor: semanticColors.disabledBackground },
                    '&:active': { transform: 'scale(0.96)' },
                    '&:focus-visible': {
                      outline: `2px solid ${semanticColors.brandPrimary}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <MicNoneIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              {/* Filled circle rather than a bare arrow: the send action is the
              composer's one primary control, so it carries the same dark fill
              as the app's other primary buttons. While a response streams it
              becomes Stop — one button whose meaning follows the state. */}
              <Tooltip title={actionLabel}>
                <span>
                  <IconButton
                    onClick={canStop ? onStop : handleSend}
                    aria-disabled={!canStop && !canSend}
                    aria-label={actionLabel}
                    data-testid={canStop ? 'stop-button' : 'send-button'}
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      color: semanticColors.textOnPrimary,
                      backgroundColor: semanticColors.textPrimary,
                      transition: 'background-color 0.2s ease, transform 0.1s ease',
                      '&:hover': { backgroundColor: semanticColors.brandAccent },
                      '&:active': { transform: 'scale(0.96)' },
                      '&.Mui-disabled': {
                        backgroundColor: semanticColors.disabledBackground,
                        color: semanticColors.textPlaceholder,
                      },
                      '&[aria-disabled="true"]': {
                        backgroundColor: semanticColors.disabledBackground,
                        color: semanticColors.textPlaceholder,
                        cursor: 'default',
                        '&:active': { transform: 'none' },
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${semanticColors.brandPrimary}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    {canStop ? (
                      <StopIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
      {voice.error && (
        /* Inline and dismissible rather than a dialog: the recording is already
           gone, so the only thing left is to say what happened and leave the
           voter in the composer to retry or type. */
        <Box
          role="alert"
          onClick={voice.clearError}
          sx={{
            mt: '6px',
            px: '4px',
            cursor: 'pointer',
            fontSize: 13,
            lineHeight: 1.4,
            color: semanticColors.error,
          }}
        >
          {voice.error}
        </Box>
      )}
      {/* Footer row: AI disclaimer centered. The character count moved into the
          composer's controls row (centered, on the send-button line). */}
      {(showHelper || ballotLoading) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 18,
            mt: '6px',
            px: '4px',
          }}
        >
          <Typography
            variant="caption"
            role="status"
            data-testid={ballotLoading ? 'ballot-loading-note' : undefined}
            sx={{
              fontSize: 12,
              lineHeight: 1.4,
              // textTertiary, not textCaption: this row sits on the blue1 page
              // background, where caption grey falls just under 4.5:1.
              color: semanticColors.textTertiary,
              textAlign: 'center',
              // The loading note is longer than the disclaimer and wraps on a phone.
              whiteSpace: ballotLoading ? 'normal' : 'nowrap',
            }}
          >
            {ballotLoading ? t('input.ballotLoading') : t('input.helperText')}
          </Typography>
        </Box>
      )}
    </Box>
  );
});
